import React, { useState, useCallback, useRef } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { html } from "@codemirror/lang-html";
import { EditorView } from "@codemirror/view";

interface HtmlEditorProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
}

interface TagButton {
  label: string;
  tag: string;
  extra?: string;
  block?: boolean;
  closeAfter?: string;
  selfClose?: boolean;
}

const TAGS: TagButton[] = [
  { label: "Negrito", tag: "strong", block: false },
  { label: "Itálico", tag: "em", block: false },
  {
    label: "Link",
    tag: 'a href=""',
    block: false,
    extra: "link",
    closeAfter: "</a>",
  },
  { label: "H2", tag: "h2", block: true },
  { label: "H3", tag: "h3", block: true },
  { label: "P", tag: "p", block: true },
  { label: "UL", tag: "ul", block: true, closeAfter: "\n  <li></li>\n</ul>" },
  { label: "OL", tag: "ol", block: true, closeAfter: "\n  <li></li>\n</ol>" },
  { label: "LI", tag: "li", block: true },
  { label: "Imagem", tag: 'img src="" alt=""', selfClose: true },
  {
    label: "TABLE",
    tag: "table",
    block: true,
    closeAfter:
      "\n  <tbody>\n    <tr>\n      <td></td>\n    </tr>\n  </tbody>\n</table>",
  },
  { label: "TR", tag: "tr", block: true, closeAfter: "\n</tr>" },
  { label: "TH", tag: "th", block: true },
  { label: "TD", tag: "td", block: true },
];

function cursorAnchor(from: number, open: string): number {
  const attrMatch = open.match(/(?:href|src)\s*=\s*"([^"]*)"/);
  if (attrMatch?.index !== undefined) {
    const quoteStart =
      attrMatch.index + open.slice(attrMatch.index).indexOf('"');
    return from + quoteStart + 1;
  }
  return from + open.length;
}

export default function HtmlEditor({
  value,
  onChange,
  label,
}: HtmlEditorProps) {
  const id = React.useId();
  const viewRef = useRef<EditorView | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const onMount = useCallback((view: EditorView) => {
    viewRef.current = view;
  }, []);

  const insertTag = useCallback(
    (tag: string, closeAfter?: string, selfClose?: boolean, extra?: string) => {
      const view = viewRef.current;
      if (!view) return;
      const { from, to } = view.state.selection.main;
      const selected = view.state.sliceDoc(from, to);
      const name = tag.split(" ")[0];
      const open = `<${tag}>`;
      const close = closeAfter ?? (selfClose ? "" : `</${name}>`);
      const replacement = selected
        ? `${open}${selected}${close}`
        : extra
          ? `${open}${extra}${close}`
          : `${open}${close}`;
      view.dispatch({
        changes: { from, to, insert: replacement },
        selection: { anchor: cursorAnchor(from, open) },
      });
      view.focus();
    },
    [viewRef],
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          {showPreview ? "Editar HTML" : "Pré-visualizar"}
        </button>
      </div>
      {showPreview ? (
        <div
          className="w-full border border-gray-300 rounded-md p-4 bg-white min-h-[200px] text-sm overflow-auto"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: value }}
        />
      ) : (
        <>
          <div className="flex flex-wrap gap-1 mb-1">
            {TAGS.map((t) => (
              <button
                key={`${t.label}-${t.tag}`}
                type="button"
                onClick={() =>
                  insertTag(t.tag, t.closeAfter, t.selfClose, t.extra)
                }
                className="px-2 py-1 text-xs font-medium bg-gray-100 border border-gray-200 rounded hover:bg-gray-200 text-gray-700 whitespace-nowrap min-h-[32px]"
                title={`<${t.tag}>`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="border border-gray-300 rounded-md overflow-hidden">
            <CodeMirror
              value={value}
              onChange={onChange}
              extensions={[html(), EditorView.lineWrapping]}
              onCreateEditor={onMount}
              theme="light"
              height="auto"
              minHeight="300px"
              basicSetup={{
                lineNumbers: true,
                foldGutter: true,
                bracketMatching: true,
                closeBrackets: true,
                indentOnInput: true,
                highlightActiveLine: true,
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}
